import React, { useMemo, useState } from "react";
import Header from "../../components/Header";
import {
  Groups,
  PaidOutlined,
  PersonAddAlt,
  TrendingUp,
} from "@mui/icons-material";
import { Box, Chip, Typography, useTheme, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ResponsivePie } from "@nivo/pie";
import OverviewChart from "../../components/OverviewChart";
import { useGetDashboardQuery } from "../../state/api";
import StatBox from "../../components/StatBox";

const STATUS_COLORS = {
  Completed: "#00c9a7",
  Verified: "#00c9a7",
  Pending: "#ffda85",
  Not_Verified_yet: "#ffda85",
  Failed: "#de4437",
  Cancelled: "#de4437",
};

const statusColor = (status) => STATUS_COLORS[status] || "#7a7f9d";

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const Dashboard = () => {
  const theme = useTheme();
  const isNonMediumScreens = useMediaQuery("(min-width: 1200px)");
  const { data, isLoading } = useGetDashboardQuery();
  const [pageSize, setPageSize] = useState(20);

  const today = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      }),
    []
  );

  const statusBreakdown = useMemo(() => {
    if (!data || !data.transactions || data.transactions.length === 0) return [];
    const counts = {};
    data.transactions.forEach((t) => {
      const label = t.status || "Unknown";
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).map(([status, count]) => ({
      id: status,
      label: status.replace(/_/g, " "),
      value: count,
      color: statusColor(status),
    }));
  }, [data]);

  const columns = [
    {
      field: "type",
      headerName: "Type",
      flex: 0.6,
      renderCell: (params) => (
        <Typography sx={{ textTransform: "capitalize", fontSize: "0.85rem" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "paymentGateway",
      headerName: "Gateway",
      flex: 0.6,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          size="small"
          label={(params.value || "Unknown").replace(/_/g, " ")}
          sx={{
            backgroundColor: `${statusColor(params.value)}22`,
            color: statusColor(params.value),
            fontWeight: 600,
            fontSize: "0.7rem",
          }}
        />
      ),
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      flex: 0.6,
      renderCell: (params) => `NRs ${Number(params.value || 0).toLocaleString()}`,
    },
    {
      field: "createdAt",
      headerName: "Date",
      flex: 0.7,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "—",
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" flexWrap="wrap" alignItems="flex-end" justifyContent="space-between" gap="0.5rem">
        <Header title="DASHBOARD" subtitle={`${getGreeting()} — here's today's pulse`} />
        <Typography sx={{ color: theme.palette.secondary[200], fontSize: "0.85rem" }}>
          {today}
        </Typography>
      </Box>

      <Box
        mt="24px"
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gap="20px"
        sx={{
          "& > div": { gridColumn: isNonMediumScreens ? undefined : "span 12" },
        }}
      >
        {/* Stat row */}
        <Box gridColumn="span 3">
          <StatBox
            title="Total Customers"
            value={data && data.totalCustomers}
            caption="All-time registered users"
            loading={isLoading}
            accent="#24baef"
            icon={<Groups />}
          />
        </Box>
        <Box gridColumn="span 3">
          <StatBox
            title="Today's Revenue"
            value={data && data.todayStats && data.todayStats.totalSales}
            caption="Since midnight"
            loading={isLoading}
            accent="#00c9a7"
            format="currency"
            icon={<PaidOutlined />}
          />
        </Box>
        <Box gridColumn="span 3">
          <StatBox
            title="New Users"
            value={data && data.thisMonthStats && data.thisMonthStats.newUsers}
            caption="Joined this month"
            loading={isLoading}
            accent="#a370d1"
            icon={<PersonAddAlt />}
          />
        </Box>
        <Box gridColumn="span 3">
          <StatBox
            title="Yearly Revenue"
            value={data && data.yearlySalesTotal}
            caption="Since Jan 1"
            loading={isLoading}
            accent="#ffda85"
            format="currency"
            icon={<TrendingUp />}
          />
        </Box>

        {/* Chart row */}
        <Box
          gridColumn="span 8"
          backgroundColor={theme.palette.background.alt}
          border="1px solid rgba(255,255,255,0.06)"
          borderRadius="0.85rem"
          p="1.25rem"
        >
          <Typography
            sx={{
              color: theme.palette.secondary[100],
              fontWeight: 700,
              fontSize: "0.95rem",
              mb: "0.75rem",
            }}
          >
            Revenue Overview
          </Typography>
          <Box height="300px">
            <OverviewChart view="sales" isDashboard={true} />
          </Box>
        </Box>

        <Box
          gridColumn="span 4"
          height="368px"
          backgroundColor={theme.palette.background.alt}
          border="1px solid rgba(255,255,255,0.06)"
          borderRadius="0.85rem"
          p="1.25rem"
          display="flex"
          flexDirection="column"
        >
          <Typography
            sx={{
              color: theme.palette.secondary[100],
              fontWeight: 700,
              fontSize: "0.95rem",
            }}
          >
            Transaction Status
          </Typography>
          <Typography
            sx={{ color: theme.palette.secondary[200], fontSize: "0.75rem", mb: "0.5rem" }}
          >
            Last {data && data.transactions ? data.transactions.length : 0} transactions
          </Typography>
          <Box height="260px" position="relative">
            {statusBreakdown.length === 0 ? (
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography sx={{ color: theme.palette.secondary[200], fontSize: "0.85rem" }}>
                  {isLoading ? "Loading…" : "No transactions yet"}
                </Typography>
              </Box>
            ) : (
              <ResponsivePie
                data={statusBreakdown}
                theme={{
                  legends: { text: { fill: theme.palette.secondary[200] } },
                  tooltip: { container: { color: theme.palette.primary.main } },
                }}
                colors={{ datum: "data.color" }}
                margin={{ top: 10, right: 10, bottom: 50, left: 10 }}
                innerRadius={0.6}
                padAngle={1.5}
                cornerRadius={4}
                activeOuterRadiusOffset={6}
                borderWidth={0}
                enableArcLinkLabels={false}
                arcLabelsSkipAngle={20}
                arcLabelsTextColor="#1e0a2f"
                legends={[
                  {
                    anchor: "bottom",
                    direction: "row",
                    translateY: 45,
                    itemWidth: 90,
                    itemHeight: 18,
                    itemTextColor: theme.palette.secondary[200],
                    symbolSize: 10,
                    symbolShape: "circle",
                  },
                ]}
              />
            )}
          </Box>
        </Box>

        {/* Recent transactions */}
        <Box gridColumn="span 12">
          <Typography
            sx={{
              color: theme.palette.secondary[100],
              fontWeight: 700,
              fontSize: "0.95rem",
              mb: "0.75rem",
            }}
          >
            Recent Transactions
          </Typography>
          <Box
            height="60vh"
            sx={{
              "& .MuiDataGrid-root": {
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "0.85rem",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[100],
                borderBottom: "none",
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: theme.palette.background.alt,
              },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[100],
                borderTop: "none",
              },
              "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                color: `${theme.palette.secondary[200]} !important`,
              },
            }}
          >
            <DataGrid
              loading={isLoading || !data}
              getRowId={(row) => row._id}
              rows={(data && data.transactions) || []}
              columns={columns}
              pagination
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
